import { ActionContext } from 'vuex';
import { RootState } from '@/store/store';
import { PendingCitationsState } from './pending-citations.state';
import {
  pendingCitationsApi,
  PendingCitationResponse
} from '@/api/pending-citations.api';
import {
  CreateCitationDto,
  UpdateCitationDto
} from '@/jbi-shared/types/citation.dto';

type Store = ActionContext<PendingCitationsState, RootState>;

export const pendingCitationsActions = {
  async getPendingCitations(
    { commit }: Store,
    documentId: number
  ): Promise<PendingCitationResponse[]> {
    try {
      commit('GET_PENDING_CITATIONS_LOADING', documentId);

      const response = await pendingCitationsApi.getPendingDocumentCitations(
        documentId
      );
      const citations = response.citations;

      commit('GET_PENDING_CITATIONS_SUCCESS', {
        documentId,
        citations
      });

      return citations;
    } catch (error) {
      commit('GET_PENDING_CITATIONS_ERROR', error);
      throw error;
    }
  },

  async addPendingCitation(
    { commit }: Store,
    payload: { documentId: number; citationData: CreateCitationDto }
  ): Promise<PendingCitationResponse> {
    try {
      commit('ADD_PENDING_CITATION_LOADING');

      const citation = await pendingCitationsApi.addCitationToPendingDocument(
        payload.documentId,
        payload.citationData
      );

      commit('ADD_PENDING_CITATION_SUCCESS', {
        documentId: payload.documentId,
        citation
      });

      return citation;
    } catch (error) {
      commit('ADD_PENDING_CITATION_ERROR', error);
      throw error;
    }
  },

  async updatePendingCitation(
    { commit }: Store,
    payload: {
      documentId: number;
      citationId: number;
      citationData: UpdateCitationDto;
    }
  ): Promise<PendingCitationResponse> {
    try {
      commit('UPDATE_PENDING_CITATION_LOADING');

      const citation = await pendingCitationsApi.updateCitation(
        payload.documentId,
        payload.citationId,
        payload.citationData
      );

      commit('UPDATE_PENDING_CITATION_SUCCESS', {
        documentId: payload.documentId,
        citation
      });

      return citation;
    } catch (error) {
      commit('UPDATE_PENDING_CITATION_ERROR', error);
      throw error;
    }
  },

  async archivePendingCitation(
    { commit, dispatch }: Store,
    payload: { documentId: number; citationId: number }
  ): Promise<void> {
    try {
      commit('ARCHIVE_PENDING_CITATION_LOADING');

      await pendingCitationsApi.archiveCitation(
        payload.documentId,
        payload.citationId
      );

      await dispatch('getPendingCitations', payload.documentId);
    } catch (error) {
      commit('ARCHIVE_PENDING_CITATION_ERROR', error);
      throw error;
    }
  },

  async restorePendingCitation(
    { commit, dispatch }: Store,
    payload: { documentId: number; citationId: number }
  ): Promise<void> {
    try {
      commit('RESTORE_PENDING_CITATION_LOADING');

      await pendingCitationsApi.restoreCitation(
        payload.documentId,
        payload.citationId
      );

      await dispatch('getPendingCitations', payload.documentId);
    } catch (error) {
      commit('RESTORE_PENDING_CITATION_ERROR', error);
      throw error;
    }
  },

  async deletePendingCitation(
    { commit }: Store,
    payload: { documentId: number; citationId: number }
  ): Promise<void> {
    try {
      commit('DELETE_PENDING_CITATION_LOADING');

      await pendingCitationsApi.deleteCitation(
        payload.documentId,
        payload.citationId
      );

      commit('DELETE_PENDING_CITATION_SUCCESS', {
        documentId: payload.documentId,
        citationId: payload.citationId
      });
    } catch (error) {
      commit('DELETE_PENDING_CITATION_ERROR', error);
      throw error;
    }
  },

  async importPendingCitations(
    { commit }: Store,
    payload: { documentId: number; citationsData: CreateCitationDto[] }
  ): Promise<PendingCitationResponse[]> {
    try {
      commit('IMPORT_PENDING_CITATIONS_LOADING');

      const citations = await pendingCitationsApi.importCitationsToPendingDocument(
        payload.documentId,
        payload.citationsData
      );

      commit('IMPORT_PENDING_CITATIONS_SUCCESS', {
        documentId: payload.documentId,
        citations
      });

      return citations;
    } catch (error) {
      commit('IMPORT_PENDING_CITATIONS_ERROR', error);
      throw error;
    }
  },

  async syncWithRevisionCitations(
    { commit, dispatch }: Store,
    payload: { documentId: number; revisionId: number }
  ): Promise<void> {
    try {
      commit('CLEAR_PENDING_CITATIONS', payload.documentId);

      // Trigger refresh of revision citations in documents store
      await dispatch(
        'documents/getRevisionCitations',
        {
          documentId: payload.documentId,
          revisionId: payload.revisionId
        },
        { root: true }
      );
    } catch (error) {
      throw error;
    }
  },

  /**
   * Clear pending citations from store (for cleanup)
   */
  clearPendingCitations({ commit }: Store, documentId?: number): void {
    commit('CLEAR_PENDING_CITATIONS', documentId);
  }
};
